import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "sonner";
import moment from "moment";
import {
  Bell, CalendarX2, CalendarCheck, Clock, CalendarOff, FlaskConical, Info, CheckCheck, MessagesSquare,
} from "lucide-react";
import { getNotifications, markNotificationRead, markAllNotificationsRead } from "../api/api";
import { getSocket } from "../lib/socket";
import { Button } from "./ui/button";
import { Skeleton } from "./ui/skeleton";
import { cn } from "../lib/utils";

const TYPE_ICON = {
  doctor_leave: CalendarX2,
  doctor_leave_cleared: CalendarCheck,
  doctor_delay: Clock,
  reschedule_required: CalendarOff,
  appointment_cancelled: CalendarX2,
  lab_report_ready: FlaskConical,
  lead_needs_human: MessagesSquare,
  lead_pending_approval: MessagesSquare,
  system: Info,
};

const SEVERITY_TONE = {
  critical: { icon: "text-destructive", dot: "bg-destructive", ring: "bg-destructive/10" },
  warning: { icon: "text-warning", dot: "bg-warning", ring: "bg-warning/10" },
  info: { icon: "text-secondary", dot: "bg-secondary", ring: "bg-secondary/10" },
};

/**
 * Notification bell.
 *
 * Polls on a slow interval as a safety net, but the live path is the Socket.IO
 * broadcast the backend already uses for call events — so a leave marked at
 * one desk lands on every other desk immediately, without waiting for a poll.
 */
export default function NotificationBell() {
  const navigate = useNavigate();
  const isLogin = useSelector((s) => s.users.is_login);

  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const panelRef = useRef(null);

  const load = useCallback(() => {
    if (!isLogin) return;
    setLoading(true);
    getNotifications({ limit: 20 })
      .then((res) => {
        setItems(res.data || []);
        setUnread(res.unread || 0);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [isLogin]);

  useEffect(() => { load(); }, [load]);

  // Slow poll purely as a fallback for a dropped socket.
  useEffect(() => {
    if (!isLogin) return;
    const id = setInterval(load, 120000);
    return () => clearInterval(id);
  }, [isLogin, load]);

  // Live delivery.
  useEffect(() => {
    if (!isLogin) return;
    const socket = getSocket();
    if (!socket) return;

    const onNotification = (n) => {
      setItems((prev) => [{ ...n, read: false }, ...prev].slice(0, 20));
      setUnread((u) => u + 1);
      // Critical notices interrupt; the rest just increment the badge.
      if (n.severity === "critical") {
        toast.error(n.title, { description: n.body, duration: 8000 });
      }
    };

    socket.on("notification", onNotification);
    return () => socket.off("notification", onNotification);
  }, [isLogin]);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const openItem = async (n) => {
    if (!n.read) {
      setItems((prev) => prev.map((i) => (i._id === n._id ? { ...i, read: true } : i)));
      setUnread((u) => Math.max(0, u - 1));
      markNotificationRead(n._id).catch(() => {});
    }
    setOpen(false);
    if (n.link) navigate(n.link);
  };

  const markAll = async () => {
    try {
      await markAllNotificationsRead();
      setItems((prev) => prev.map((i) => ({ ...i, read: true })));
      setUnread(0);
    } catch {
      toast.error("Could not mark all as read.");
    }
  };

  return (
    <div className="relative" ref={panelRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={unread ? `Notifications, ${unread} unread` : "Notifications"}
        className="relative rounded-md p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
      >
        <Bell className="h-5 w-5" />
        {unread > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold tabular-nums text-primary-foreground">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 z-50 mt-2 w-[22rem] overflow-hidden rounded-lg border border-border bg-popover shadow-lg">
          <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
            <p className="text-sm font-medium">
              Notifications
              {unread > 0 && <span className="ml-1.5 text-muted-foreground">({unread} new)</span>}
            </p>
            {unread > 0 && (
              <Button variant="ghost" size="sm" className="h-7 gap-1 px-2 text-xs" onClick={markAll}>
                <CheckCheck className="h-3.5 w-3.5" /> Mark all read
              </Button>
            )}
          </div>

          <div className="max-h-[26rem] overflow-y-auto">
            {loading && items.length === 0 ? (
              <div className="space-y-2 p-3">
                <Skeleton className="h-14 w-full" />
                <Skeleton className="h-14 w-full" />
              </div>
            ) : items.length === 0 ? (
              <div className="px-4 py-10 text-center">
                <Bell className="mx-auto h-6 w-6 text-muted-foreground/40" />
                <p className="mt-2 text-sm text-muted-foreground">Nothing to catch up on.</p>
              </div>
            ) : (
              items.map((n) => {
                const Icon = TYPE_ICON[n.type] || Info;
                const tone = SEVERITY_TONE[n.severity] || SEVERITY_TONE.info;
                return (
                  <button
                    key={n._id}
                    type="button"
                    onClick={() => openItem(n)}
                    className={cn(
                      "flex w-full gap-3 border-b border-border px-4 py-3 text-left transition-colors last:border-0 hover:bg-muted/50",
                      !n.read && "bg-primary/[0.04]"
                    )}
                  >
                    <span className={cn("mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full", tone.ring)}>
                      <Icon className={cn("h-3.5 w-3.5", tone.icon)} />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-start gap-2">
                        <span className={cn("block text-sm leading-snug", !n.read && "font-medium")}>
                          {n.title}
                        </span>
                        {!n.read && (
                          <span className={cn("mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full", tone.dot)} />
                        )}
                      </span>
                      {n.body && (
                        <span className="mt-0.5 block text-xs leading-snug text-muted-foreground">
                          {n.body}
                        </span>
                      )}
                      <span className="mt-1 block text-[11px] text-muted-foreground">
                        {moment(n.createdAt).fromNow()}
                        {n.created_by_name && n.created_by_name !== "System" ? ` · ${n.created_by_name}` : ""}
                      </span>
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
